import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { reportGlobalError } from '@/utils/GlobalErrorReporter'

const FIVE_MINUTES_MS = 5 * 60 * 1000
const TEN_MINUTES_MS = 10 * 60 * 1000
const SINGLE_RETRY = 1

/**
 * Determines if an error should trigger the error report dialog.
 * Only reports server errors (5xx) - client errors (4xx) are handled by components.
 */
function shouldReportQueryError(error: unknown): boolean {
	if (!(error instanceof Error)) return false

	const message = error.message.toLowerCase()

	// Don't report expected client errors
	const clientErrors = ['unauthorized', 'access_denied', 'not_found', '401', '403', '404']
	if (clientErrors.some((e) => message.includes(e))) {
		return false
	}

	// Report server errors (5xx) and unexpected failures
	const serverErrors = ['500', '502', '503', '504', 'internal server', 'server error']
	if (serverErrors.some((e) => message.includes(e))) {
		return true
	}

	// Report network failures that aren't simple connectivity issues
	if (message.includes('failed to fetch') || message.includes('network')) {
		return false // Don't spam for network issues
	}

	return false
}

const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: (error) => {
			if (shouldReportQueryError(error)) {
				reportGlobalError(error)
			}
		},
	}),
	mutationCache: new MutationCache({
		onError: (error) => {
			if (shouldReportQueryError(error)) {
				reportGlobalError(error)
			}
		},
	}),
	defaultOptions: {
		queries: {
			// Stale time: 5 minutes (formations/concepts don't change often)
			staleTime: FIVE_MINUTES_MS,
			// Cache time: 10 minutes
			gcTime: TEN_MINUTES_MS,
			// Retry failed requests once
			retry: SINGLE_RETRY,
			// Refetch on window focus for fresh data
			refetchOnWindowFocus: true,
			// Don't refetch on mount if data is fresh
			refetchOnMount: false,
		},
		mutations: {
			// Retry failed mutations once
			retry: SINGLE_RETRY,
		},
	},
})

type QueryProviderProps = {
	children: ReactNode
}

export function QueryProvider({ children }: QueryProviderProps) {
	return (
		<QueryClientProvider client={queryClient}>
			{children}
		</QueryClientProvider>
	)
}

export { queryClient }
